"use client";

import Link from "next/link";

import { FormError } from "@/components/forms/form-error";
import { OAuthProviderButtons } from "@/components/forms/oauth-provider-buttons";
import { buttonVariants } from "@/components/ui/button";
import { withNext } from "@/lib/auth/safe-next";
import { onboardingUrl } from "@/lib/onboarding/routes";
import { cn } from "@/lib/utils";

import {
  onboardingWithAppleAction,
  onboardingWithGoogleAction,
} from "../../actions";
import type { StepProps } from "../types";
import { StepFooter, StepHeader } from "../ui";

const ACTIONS = {
  apple: onboardingWithAppleAction,
  google: onboardingWithGoogleAction,
};

/** Sign-up, placed AFTER the questions (and, for students, after their plan).
 *  Every path returns to /onboarding (keeping any pending deep link, on
 *  success AND failure), where the wizard resumes from the draft at the step
 *  after this one:
 *  - provider buttons: OAuth (web redirect, or the shell's native sheet);
 *  - email: the existing /signup form, whose confirmation link carries the
 *    same `next`. If that link is opened in another browser the draft isn't
 *    there, and the flow re-asks only the required answers (flow.ts).
 *  The provider list itself is App Review guideline 4.8 data, decided by
 *  OAuthProviderButtons; never render provider buttons directly. */
export function AccountStep({ state, env }: StepProps) {
  const back = onboardingUrl(env.next);
  const instructor = state.answers.audience === "instructor";
  return (
    <>
      <StepHeader
        kicker="Almost done"
        title="Create your account."
        body={
          instructor
            ? "So you can come back anytime. Class tools are set up with your program after signup."
            : "So your progress and scores follow you to any device."
        }
      />
      <div className="mt-7 space-y-3">
        {env.oauthError ? <FormError message={env.oauthError} /> : null}
        <OAuthProviderButtons
          actions={ACTIONS}
          verb="Sign up"
          next={back}
          errorReturnPath={back}
          shell={env.shell}
        />
        <Link
          href={withNext("/signup", back)}
          className={cn(
            buttonVariants({ variant: "outline", size: "lg" }),
            "h-12 w-full rounded-xl text-base",
          )}
        >
          Continue with email
        </Link>
      </div>
      <StepFooter>
        <p className="py-3 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link href={withNext("/login", back)} className="text-primary underline">
            Sign in
          </Link>
        </p>
      </StepFooter>
    </>
  );
}
